// src/admin/pages/DatabaseToolsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Database, 
  RefreshCw, 
  Search,
  Eye,
  Edit,
  Trash2,
  X,
  Save,
  Table as TableIcon,
  FileText,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import { supabase } from '../../config';

const DatabaseToolsPage = () => {
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'browser', or 'schema'
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [schemaData, setSchemaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [dbConnectionStatus, setDbConnectionStatus] = useState(null);
  const recordsPerPage = 50;

  // Define available tables to browse
  const availableTables = [
    { name: 'user_profiles', label: 'User Profiles', description: 'All user profile information' },
    { name: 'equity_companies', label: 'Companies', description: 'Company records' },
    { name: 'equity_shareholders', label: 'Shareholders', description: 'Shareholder information' },
    { name: 'equity_directors', label: 'Directors', description: 'Company directors' },
    { name: 'equity_share_classes', label: 'Share Classes', description: 'Share class definitions' },
    { name: 'equity_share_issuances', label: 'Share Issuances', description: 'Share issuance records' },
    { name: 'dataroom_projects', label: 'Dataroom Projects', description: 'Dataroom projects' },
    { name: 'dataroom_users', label: 'Dataroom Users', description: 'Dataroom user access' },
    { name: 'countries', label: 'Countries', description: 'Country data' },
    { name: 'subscription_plans', label: 'Subscription Plans', description: 'Available subscription plans' },
    { name: 'promo_codes', label: 'Promo Codes', description: 'Promotional codes' }
  ];

  // Check database connection
  const checkDatabaseConnection = async () => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .select('id')
        .limit(1);

      setDbConnectionStatus({
        connected: !error,
        status: error ? 'disconnected' : 'connected',
        message: error ? 'Database connection failed' : 'Database connected',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setDbConnectionStatus({
        connected: false,
        status: 'error',
        message: error.message || 'Database check failed',
        timestamp: new Date().toISOString()
      });
    }
  };

  // Fetch table statistics
  const fetchTableStats = async () => {
    setLoading(true);
    const stats = [];
    
    try {
      // Check database connection first
      await checkDatabaseConnection();

      for (const table of availableTables) {
        try {
          const { count, error } = await supabase
            .from(table.name)
            .select('*', { count: 'exact', head: true });
          
          // Handle 404 errors (table doesn't exist) silently
          if (error && error.code === 'PGRST116') {
            // Table doesn't exist - skip it or mark as unavailable
            stats.push({
              ...table,
              count: 0,
              exists: false
            });
          } else if (error) {
            // Other errors - log but continue
            console.warn(`Warning: Could not fetch stats for ${table.name}:`, error.message);
            stats.push({
              ...table,
              count: 0,
              exists: true
            });
          } else {
            stats.push({
              ...table,
              count: count || 0,
              exists: true
            });
          }
        } catch (err) {
          // Handle network or other errors
          if (err.code === 'PGRST116' || err.message?.includes('404')) {
            stats.push({
              ...table,
              count: 0,
              exists: false
            });
          } else {
            console.warn(`Warning: Error fetching stats for ${table.name}:`, err.message);
            stats.push({
              ...table,
              count: 0,
              exists: true
            });
          }
        }
      }
      
      setTables(stats);
    } catch (error) {
      console.error('Error fetching table stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch table data
  const fetchTableData = async (tableName, page = 1) => {
    setLoading(true);
    try {
      const from = (page - 1) * recordsPerPage;
      const to = from + recordsPerPage - 1;

      // Get total count
      const { count, error: countError } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });
      
      // Handle 404 errors (table doesn't exist)
      if (countError && (countError.code === 'PGRST116' || countError.message?.includes('404'))) {
        setTableData([]);
        setTableColumns([]);
        setTotalRecords(0);
        setLoading(false);
        return;
      }
      
      setTotalRecords(count || 0);

      // Get paginated data
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .range(from, to)
        .order('created_at', { ascending: false });

      // Handle 404 errors
      if (error && (error.code === 'PGRST116' || error.message?.includes('404'))) {
        setTableData([]);
        setTableColumns([]);
        setTotalRecords(0);
        return;
      }

      if (error) throw error;

      setTableData(data || []);
      
      // Extract columns from first record
      if (data && data.length > 0) {
        const columns = Object.keys(data[0]);
        setTableColumns(columns);
      } else {
        setTableColumns([]);
      }
    } catch (error) {
      // Only log non-404 errors
      if (!error.code || (error.code !== 'PGRST116' && !error.message?.includes('404'))) {
        console.error('Error fetching table data:', error);
      }
      setTableData([]);
      setTableColumns([]);
    } finally {
      setLoading(false);
    }
  };

  // Search in table
  const searchTableData = async (tableName, searchTerm) => {
    if (!searchTerm) {
      fetchTableData(tableName, currentPage);
      return;
    }

    setLoading(true);
    try {
      // Search in all text columns
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(recordsPerPage);

      if (error) throw error;

      setTableData(data || []);
      setTotalRecords(data?.length || 0);
    } catch (error) {
      console.error('Error searching table:', error);
      // Fallback to fetching all data
      fetchTableData(tableName, currentPage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === 'overview') {
      fetchTableStats();
    }
    // Check database connection on mount
    checkDatabaseConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => {
    if (selectedTable) {
      if (searchTerm) {
        searchTableData(selectedTable.name, searchTerm);
      } else {
        fetchTableData(selectedTable.name, currentPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable, currentPage]);

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    setActiveView('browser');
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleSearch = () => {
    if (selectedTable) {
      setCurrentPage(1);
      searchTableData(selectedTable.name, searchTerm);
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from(selectedTable.name)
        .delete()
        .eq('id', record.id);

      if (error) throw error;

      alert('Record deleted successfully');
      fetchTableData(selectedTable.name, currentPage);
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Error deleting record: ' + error.message);
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setEditedData({ ...record });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      const { error } = await supabase
        .from(selectedTable.name)
        .update(editedData)
        .eq('id', selectedRecord.id);

      if (error) throw error;

      alert('Record updated successfully');
      setEditMode(false);
      setSelectedRecord(null);
      fetchTableData(selectedTable.name, currentPage);
    } catch (error) {
      console.error('Error updating record:', error);
      alert('Error updating record: ' + error.message);
    }
  };

  const exportTableData = () => {
    if (tableData.length === 0) return;

    const csv = [
      tableColumns.join(','),
      ...tableData.map(row => 
        tableColumns.map(col => {
          const val = row[col];
          if (val === null || val === undefined) return '';
          if (typeof val === 'object') return JSON.stringify(val).replace(/,/g, ';');
          return String(val).replace(/,/g, ';');
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedTable.name}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' && value.length > 50) return value.substring(0, 47) + '...';
    return String(value);
  };

  // Fetch database schema
  const fetchDatabaseSchema = async () => {
    setLoading(true);
    try {
      // Instead of querying information_schema, inspect actual tables
      const tableSchema = {};

      for (const table of availableTables) {
        try {
          // Get a sample row to determine column structure
          const { data } = await supabase
            .from(table.name)
            .select('*')
            .limit(1)
            .single();

          if (data) {
            // Extract column information from the data structure
            const columns = Object.keys(data).map((columnName, index) => {
              const value = data[columnName];
              let dataType = typeof value;
              
              // Better type detection
              if (value === null) dataType = 'nullable';
              else if (typeof value === 'number') {
                dataType = Number.isInteger(value) ? 'integer' : 'numeric';
              } else if (typeof value === 'boolean') dataType = 'boolean';
              else if (typeof value === 'string') {
                // Check if it looks like a date
                if (/^\d{4}-\d{2}-\d{2}/.test(value)) dataType = 'timestamp';
                else if (value.length === 36 && value.includes('-')) dataType = 'uuid';
                else dataType = 'text';
              } else if (typeof value === 'object') dataType = 'jsonb';

              return {
                name: columnName,
                type: dataType,
                nullable: value === null,
                default: null,
                position: index + 1
              };
            });

            tableSchema[table.name] = {
              name: table.name,
              label: table.label,
              description: table.description,
              columns: columns
            };
          } else {
            // Empty table - get count to confirm existence
            const { count } = await supabase
              .from(table.name)
              .select('*', { count: 'exact', head: true });

            tableSchema[table.name] = {
              name: table.name,
              label: table.label,
              description: table.description,
              columns: [],
              isEmpty: true,
              count: count || 0
            };
          }
        } catch (tableError) {
          console.error(`Error inspecting ${table.name}:`, tableError);
        }
      }

      setSchemaData(tableSchema);
      console.log('Database schema loaded:', Object.keys(tableSchema).length, 'tables');
    } catch (error) {
      console.error('Error fetching schema:', error);
      alert('Error loading database schema: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'disconnected':
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <Database className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-green-600 bg-green-100';
      case 'disconnected':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Schema View
  if (activeView === 'schema') {
    return (
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Database Schema</h2>
            <p className="text-gray-600 mt-1">Complete database structure and table definitions</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setActiveView('overview')}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Overview
            </button>
            <button
              onClick={fetchDatabaseSchema}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Schema
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading schema...</p>
          </div>
        ) : schemaData ? (
          <div className="space-y-4">
            {Object.values(schemaData).map((table) => (
              <div key={table.name} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {table.label || table.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {table.description} • {table.columns.length} columns
                      {table.isEmpty && ` • Empty table (${table.count || 0} records)`}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setActiveView('browser');
                      const tableInfo = availableTables.find(t => t.name === table.name);
                      if (tableInfo) setSelectedTable(tableInfo);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Browse Data →
                  </button>
                </div>
                
                <div className="overflow-x-auto w-full max-w-full">
                  <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Column Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data Type</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nullable</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Default</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {table.columns.map((col) => (
                        <tr key={col.name} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm text-gray-500">{col.position}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900 font-mono">
                            {col.name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                            {col.type}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {col.nullable ? (
                              <span className="text-green-600">✓ Yes</span>
                            ) : (
                              <span className="text-red-600">✗ No</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600 font-mono max-w-xs truncate">
                            {col.default || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Database className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Schema Loaded</h3>
            <p className="mt-1 text-sm text-gray-500">Click "Refresh Schema" to load the database structure</p>
          </div>
        )}
      </div>
    );
  }

  // Overview View
  if (activeView === 'overview') {
    return (
      <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Database Overview</h2>
          <p className="text-gray-600 mt-1">Monitor and manage database tables</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              setActiveView('schema');
              fetchDatabaseSchema();
            }}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            <Database className="h-4 w-4 mr-2" />
            View Schema
          </button>
          <button
            onClick={fetchTableStats}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Database Connection Status */}
      {dbConnectionStatus && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Database className="h-6 w-6 text-gray-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Database Connection</h3>
                <p className="text-sm text-gray-500">{dbConnectionStatus.message}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusIcon(dbConnectionStatus.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(dbConnectionStatus.status)}`}>
                {dbConnectionStatus.status}
              </span>
            </div>
          </div>
        </div>
      )}

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <TableIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tables</p>
                <p className="text-2xl font-bold text-gray-900">{tables.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Database className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tables.reduce((sum, t) => sum + t.count, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Largest Table</p>
                <p className="text-lg font-bold text-gray-900">
                  {tables.length > 0 ? tables.reduce((max, t) => t.count > max.count ? t : max, tables[0]).label : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tables Grid */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900">Database Tables</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading tables...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {tables.map((table) => (
                <div
                  key={table.name}
                  onClick={() => handleTableSelect(table)}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <TableIcon className="h-5 w-5 text-blue-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">{table.label}</h4>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {table.count.toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{table.description}</p>
                  <p className="text-xs text-gray-500 font-mono">{table.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Browser View
  return (
    <div className="w-full max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={() => {
              setActiveView('overview');
              setSelectedTable(null);
              setTableData([]);
              setSearchTerm('');
            }}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{selectedTable?.label}</h2>
            <p className="text-gray-600 mt-1">{selectedTable?.description} • {totalRecords.toLocaleString()} records</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportTableData}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => fetchTableData(selectedTable.name, currentPage)}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center">
          <Search className="h-5 w-5 text-gray-400 mr-3" />
          <input
            type="text"
            placeholder="Search records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 outline-none text-gray-700"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                fetchTableData(selectedTable.name, currentPage);
              }}
              className="text-gray-400 hover:text-gray-600 mr-3"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            Search
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading data...</p>
          </div>
        ) : tableData.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No records found</h3>
            <p className="mt-1 text-sm text-gray-500">This table is empty or no records match your search.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto w-full max-w-full">
              <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'auto' }}>
                <thead className="bg-gray-50">
                  <tr>
                    {tableColumns.slice(0, 8).map((column) => (
                      <th
                        key={column}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column}
                      </th>
                    ))}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableData.map((record, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      {tableColumns.slice(0, 8).map((column) => (
                        <td key={column} className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {formatValue(record[column])}
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setSelectedRecord(record)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <Eye className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleEditRecord(record)}
                          className="text-green-600 hover:text-green-900 mr-3"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(record)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords.toLocaleString()} records
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View Record Modal */}
      {selectedRecord && !editMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Record Details</h3>
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-auto">
                <pre className="text-xs text-gray-900 bg-gray-50 p-4 rounded-md">
                  {JSON.stringify(selectedRecord, null, 2)}
                </pre>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => handleEditRecord(selectedRecord)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Record Modal */}
      {editMode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Edit Record</h3>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setSelectedRecord(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-auto space-y-4">
                {Object.keys(editedData).map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {key}
                    </label>
                    {key === 'id' || key === 'created_at' || key === 'updated_at' ? (
                      <input
                        type="text"
                        value={editedData[key] || ''}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
                      />
                    ) : (
                      <input
                        type="text"
                        value={typeof editedData[key] === 'object' ? JSON.stringify(editedData[key]) : (editedData[key] || '')}
                        onChange={(e) => setEditedData({ ...editedData, [key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={handleSaveEdit}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setSelectedRecord(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseToolsPage;

